<template>
  <div style="display: flex; flex-flow: row; width: 100%; grid-gap: 20px;">
    <basic-card>
      <template slot="header">
        {{ displayName }} [Showing in {{ targetCurrency }}]
      </template>

      <svg style="width: 100%; height: 100%; min-height: 500px;" ref="map">
      </svg>

    </basic-card>
    <basic-card v-if="selectedFeature">
      <template #header>
        Detail of {{selectedFeature.properties.brk_name}}
      </template>
      <data-table
          :configuration="accountsTableConfig"
          :data="selectedFeatureAccounts"
          :component-id="`dashboard-widget-balances-[${displayName}]-details`"
      />
    </basic-card>
  </div>
</template>

<script>
import BasicCard from "@/components/layout/BasicCard";
import Axios from "axios";
import * as d3 from 'd3'
import {mapGetters} from "vuex";
import BankingStore from "@/stores/banking-store";
import DataTable from "@/components/table/DataTable";

export default {
  name: "GeoAccountBalancesWidget",
  components: {DataTable, BasicCard},
  props: {
    displayName: String,
    accountQuery: String,
    dateExpr: String,
    targetCurrency: String
  },
  data: function () {
    return {
      mapProjection: {},
      geoGenerator: {},
      selectedFeature: null,
      accountsTableConfig: {
        objectName: "Account",
        columns: [
          {
            caption: "Owner",
            datum: d => {
              let owner = this.accountOwnerships.filter(accOwnership => accOwnership.accountId === d.id);
              if (owner.length == 0) return "N/A";
              else return owner[0].partyReference.displayName
            },
            sortField: "owner",
            allowSort: true,
            style: (d) => ({'white-space': 'nowrap'}),
          },
          {
            caption: "Name",
            datum: d => d.name,
            style: (d) => ({'text-align': 'left'}),
            sortField: "name",
            allowSort: true
          },
          {
            caption: "Last balance date",
            datum: d => this.accountBalances(d)?.length ?? 0 > 0 ? new Date(this.accountBalances(d)[0].balanceDate) : new Date(0),
            datumFormat: d => d.getTime() == 0 ? "N/A" : d.toLocaleDateString(),
            style: (d) => ({'text-align': 'left'}),
            allowSort: true,
            sortField: "balanceDate",
            sortType: "date"
          },
          {
            caption: "CCY",
            datum: d => d.currency,
            style: (d) => ({'text-align': 'left'}),
            sortField: "currency",
            allowSort: true
          },
          {
            caption: "Actual balance",
            datum: d => d.balance,
            datumFormat: d => this.$formats.formatAmount(d),
            style: (d) => ({'text-align': 'right'}),
            sortField: "actualBalance",
            sortType: "number",
            allowSort: true
          }
        ]
      }
    }
  },
  computed: {
    ...mapGetters({
      accountOwnerships: "accountOwnerships/accountOwnerships",
      accountBalances: 'banking/accountBalances',
      getValuesForCurrency: "currencies/getValuesForCurrency"
    }),
    selectedFeatureAccounts() {
        if(this.selectedFeature)
          return [ ... this.accounts].filter(acc => d3.geoContains(this.selectedFeature, [acc.geoPoint.longtitude, acc.geoPoint.latitude]));
        else
          return this.accounts;
    },
    accounts() {
      let accs = this.$store.getters[`${this.displayName}/accounts`];
      accs = accs.map(acc => ({ ... acc, balance: typeof this.accountBalances(acc) != 'undefined' && this.accountBalances(acc).length > 0 ? this.accountBalances(acc)[0].endBalance / 100 : 0}));
      return accs ?? [];
    }
  },
  methods: {
    async drawMap() {

      const balances = this.accounts.map(acc => {
        let rates = this.getValuesForCurrency(acc.currency);
        return acc.balance * (rates.length == 0 ? 1 : rates[0].value / 1000)
      });


      const defaultScale = d3.scaleLinear().domain([0, d3.max(balances)]).range(["#454b50", "#C64B4D"])

      const geoData = (await Axios.get("custom.geo.json")).data;

      const graticule = d3.geoGraticule10();

      const allAccounts = this.accounts;

      this.mapProjection = d3.geoOrthographic().rotate([2.5, -10, 0]).scale(500).center([15, 53])
      this.geoGenerator = d3.geoPath().projection(this.mapProjection);

      const setSelectedFeature = (param) => this.selectedFeature = param;

      const mapClick = function (d) {
        d3.select(".selected-map-feature").classed("selected-map-feature", false);
        d3.select(this).classed("selected-map-feature", true);
        setSelectedFeature(d);
      }

      d3.select(this.$refs.map).append("path")
          .datum(graticule)
          .attr("class", "graticule")
          .attr("d", this.geoGenerator);

      let u = d3.select(this.$refs.map)
          .selectAll("path")
          .data(geoData.features);

      u.enter()
          .append('path')
          .attr('fill', d => {
            let accsInFeature = [ ... allAccounts].filter(acc => d3.geoContains(d, [acc.geoPoint.longtitude, acc.geoPoint.latitude]));
            const sum = d3.sum(accsInFeature, acc => {
              let rates = this.getValuesForCurrency(acc.currency);
              return acc.balance * (rates.length == 0 ? 1 : rates[0].value / 1000)
            });

            return defaultScale(sum);
          })
          .attr('stroke', 'white')
          .attr('d', this.geoGenerator)
          .on('click', mapClick)
    }
  },
  async mounted() {
    if (this.$store._modules.root._children[this.displayName] == undefined) {
      this.$store.registerModule(this.displayName, BankingStore());
    }
    await this.$store.dispatch(`accountQueries/refreshAccountQueries`);
    let fullQuery = this.$store.getters[`accountQueries/queryById`](this.accountQuery);
    await this.$store.dispatch(`${this.displayName}/refreshAccountsPaged`, {page: 0, size: 500, query: fullQuery})
    this.$forceUpdate();
    await this.drawMap();
  },
  watch: {
    selectedFeature: {
      handler(newVal) {

      }
    }
  }
}
</script>

<style lang="scss">
@import "../../../styles/global";

.map-feature {
  fill: #454b50;
}

.selected-map-feature {
  color: white;
  stroke-width: 3px;
  z-index: 1000;
}

.graticule {
  fill: none;
  stroke: #5a5f63;
  stroke-width: 1px;
}
</style>